<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar
            color="bg-primary-lighten-1"
            density="compact"
            class="px-5 py-3"
          >
            <v-icon
              icon="mdi-account-cog"
              size="large"
              class="me-2"
            ></v-icon>
            <v-toolbar-title class="text-h5 font-weight-medium">
              {{ $t('settings.profileInfo') }}
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text v-if="loading">
            <div class="d-flex justify-center align-center pa-6">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </div>
          </v-card-text>

          <v-card-text v-else>
            <v-alert
              v-if="userStore.error"
              type="error"
              class="mb-4"
            >
              {{ userStore.error }}
            </v-alert>

            <v-list v-if="userStore.profile">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-email" size="small"></v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ $t('settings.email') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ userStore.profile?.email }}</v-list-item-subtitle>
                  </v-list-item>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-account" size="small"></v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ $t('settings.fullName') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ getFullName(userStore.profile) || $t('settings.notSpecified') }}</v-list-item-subtitle>
                  </v-list-item>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-shield-account" size="small"></v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ $t('settings.role') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ $t(`settings.roles.${userStore.profile?.role}`) }}</v-list-item-subtitle>
                  </v-list-item>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-list-item>
                    <template v-slot:prepend>
                      <v-icon icon="mdi-calendar" size="small"></v-icon>
                    </template>
                    <v-list-item-title class="text-body-2">{{ $t('settings.dateJoined') }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ formatDate(userStore.profile?.date_joined) }}</v-list-item-subtitle>
                  </v-list-item>
                </v-col>
              </v-row>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar
            color="bg-primary-lighten-1"
            density="compact"
            class="px-5 py-3"
          >
            <v-icon
              icon="mdi-cog"
              size="large"
              class="me-2"
            ></v-icon>
            <v-toolbar-title class="text-h5 font-weight-medium">
              {{ $t('settings.appSettings') }}
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text v-if="loading">
            <div class="d-flex justify-center align-center pa-6">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </div>
          </v-card-text>

          <v-card-text v-else>
            <v-list>
              <!-- Theme Settings -->
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon>{{ isDark ? 'mdi-weather-night' : 'mdi-weather-sunny' }}</v-icon>
                </template>
                <v-list-item-title>{{ $t('settings.theme') }}</v-list-item-title>
                <template v-slot:append>
                  <v-switch
                    v-model="isDark"
                    hide-details
                    density="compact"
                    color="primary"
                    @update:model-value="toggleTheme"
                  ></v-switch>
                </template>
              </v-list-item>

              <v-divider class="my-3"></v-divider>

              <!-- Language Settings -->
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon>mdi-translate</v-icon>
                </template>
                <v-list-item-title>{{ $t('system.language') }}</v-list-item-title>
                <template v-slot:append>
                  <v-select
                    v-model="currentLocale"
                    :items="availableLocales"
                    item-title="name"
                    item-value="code"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="language-select"
                    @update:model-value="changeLocale"
                  ></v-select>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Community settings -->
    <v-row class="mt-6">
      <v-col cols="12">
        <v-card>
          <v-toolbar
            color="bg-primary-lighten-1"
            density="compact"
            class="px-5 py-3"
          >
            <v-icon
              icon="mdi-office-building-cog"
              size="large"
              class="me-2"
            ></v-icon>
            <v-toolbar-title class="text-h5 font-weight-medium">
              {{ $t('settings.communitySettings') }}
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text v-if="loading">
            <div class="d-flex justify-center align-center pa-6">
              <v-progress-circular
                indeterminate
                color="primary"
                size="64"
              ></v-progress-circular>
            </div>
          </v-card-text>

          <v-card-text v-else>
            <v-alert
              v-if="error"
              type="error"
              class="mb-4"
            >
              {{ error }}
            </v-alert>

            <div v-if="communities.length === 0" class="text-center py-4">
              {{ $t('settings.noCommunities') }}
            </div>

            <template v-else>
              <v-select
                v-if="communities.length > 1"
                v-model="selectedCommunityId"
                :items="communities"
                item-title="title"
                item-value="value"
                :label="$t('settings.selectCommunity')"
                variant="outlined"
                class="mb-6"
                @update:model-value="handleCommunityChange"
              ></v-select>

              <v-form
                v-if="currentCommunityId"
                ref="communityForm"
                v-model="formValid"
              >
                <!-- General Settings -->
                <div class="settings-section">
                  <div class="section-title">
                    <v-icon icon="mdi-cog" class="me-2"></v-icon>
                    {{ $t('settings.generalSection') }}
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.name"
                        :label="$t('settings.communityName')"
                        variant="outlined"
                        density="comfortable"
                        :rules="[v => !!v || $t('validation.required')]"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.address"
                        :label="$t('settings.communityAddress')"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-switch
                        v-model="editedSettings.auto_approve_users"
                        :label="$t('settings.autoApproveUsers')"
                        color="primary"
                        hide-details
                      ></v-switch>
                    </v-col>
                  </v-row>
                </div>

                <!-- Limits Settings -->
                <div class="settings-section">
                  <div class="section-title">
                    <v-icon icon="mdi-gauge" class="me-2"></v-icon>
                    {{ $t('settings.limitsSection') }}
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.max_cells_per_customer"
                        :label="$t('settings.maxCellsPerCustomer')"
                        :hint="$t('settings.maxCellsPerCustomerHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="1"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.max_sharing_hours"
                        :label="$t('settings.maxSharingHours')"
                        :hint="$t('settings.maxSharingHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="1"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.max_delivery_hours"
                        :label="$t('settings.maxDeliveryHours')"
                        :hint="$t('settings.maxDeliveryHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="1"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.max_storage_hours"
                        :label="$t('settings.maxStorageHours')"
                        :hint="$t('settings.maxStorageHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="1"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.min_storage_hours"
                        :label="$t('settings.minStorageHours')"
                        :hint="$t('settings.minStorageHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="1"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Pricing Settings -->
                <div class="settings-section">
                  <div class="section-title">
                    <v-icon icon="mdi-currency-usd" class="me-2"></v-icon>
                    {{ $t('settings.pricingSection') }}
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.delivery_hourly_price"
                        :label="$t('settings.deliveryHourlyPrice')"
                        :hint="$t('settings.deliveryHourlyPriceHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        step="0.01"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.storage_hourly_price"
                        :label="$t('settings.storageHourlyPrice')"
                        :hint="$t('settings.storageHourlyPriceHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        step="0.01"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.sharing_hourly_price"
                        :label="$t('settings.sharingHourlyPrice')"
                        :hint="$t('settings.sharingHourlyPriceHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        step="0.01"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.free_delivery_hours"
                        :label="$t('settings.freeDeliveryHours')"
                        :hint="$t('settings.freeDeliveryHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.free_storage_hours"
                        :label="$t('settings.freeStorageHours')"
                        :hint="$t('settings.freeStorageHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editedSettings.free_sharing_hours"
                        :label="$t('settings.freeSharingHours')"
                        :hint="$t('settings.freeSharingHoursHint')"
                        variant="outlined"
                        density="comfortable"
                        type="number"
                        min="0"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-switch
                        v-model="editedSettings.price_by_slot"
                        :label="$t('settings.priceBySlot')"
                        :hint="$t('settings.priceBySlotHint')"
                        color="primary"
                        persistent-hint
                      ></v-switch>
                    </v-col>
                  </v-row>
                </div>

                <div class="d-flex justify-end mt-6">
                  <v-btn
                    color="primary"
                    :disabled="!hasChanges"
                    @click="saveCommunitySettings"
                  >
                    {{ $t('common.save') }}
                  </v-btn>
                </div>
              </v-form>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Диалог подтверждения сохранения -->
    <v-dialog v-model="showConfirmDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5 pa-4">
          {{ $t('settings.confirmSaveTitle') }}
        </v-card-title>
        <v-card-text>
          {{ $t('settings.confirmSaveText') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="showConfirmDialog = false"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            :loading="saving"
            @click="confirmSave"
          >
            {{ $t('common.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTheme } from '@/composables/useTheme'
import { useUserStore } from '@/stores/user'
import api from '@/services/api'

const { locale, t } = useI18n()
const { isDark, toggleTheme } = useTheme()
const userStore = useUserStore()

const communities = ref([])
const selectedCommunityId = ref(null)
const editedSettings = ref({})
const originalSettings = ref({})
const loading = ref(false)
const saving = ref(false)
const formValid = ref(true)
const error = ref('')
const communityForm = ref(null)
const showConfirmDialog = ref(false)

const availableLocales = [
  { code: 'ru', name: 'Русский' },
  { code: 'en', name: 'English' },
  { code: 'vi', name: 'Tiếng Việt' }
]

const currentLocale = computed(() => locale.value)

const changeLocale = (newLocale) => {
  locale.value = newLocale
  localStorage.setItem('locale', newLocale)
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date)
}

const getFullName = (user) => {
  if (!user) return ''
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`
  } else if (user.first_name) {
    return user.first_name
  } else if (user.last_name) {
    return user.last_name
  }
  return ''
}

// Вычисляемое свойство для определения текущего сообщества
const currentCommunityId = computed(() => {
  if (communities.value.length === 1) {
    return communities.value[0].value
  }
  return selectedCommunityId.value
})

const fetchCommunitySettings = async (communityId) => {
  loading.value = true
  error.value = ''
  try {
    const response = await api.get(`/api/v1/community/${communityId}/`)
    editedSettings.value = { ...response.data }
    originalSettings.value = { ...response.data }
  } catch (error) {
    console.error('Failed to fetch community settings:', error)
    error.value = error.response?.data?.detail || 'Failed to fetch community settings'
  } finally {
    loading.value = false
  }
}

const fetchCommunities = async () => {
  try {
    const response = await api.get('/api/v1/community/')
    communities.value = response.data.results.map(community => ({
      title: community.name || community.title,
      value: community.id
    }))
    
    if (communities.value.length === 1) {
      selectedCommunityId.value = communities.value[0].value
      await fetchCommunitySettings(communities.value[0].value)
    }
  } catch (error) {
    console.error('Failed to fetch communities:', error)
  }
}

const handleCommunityChange = async (communityId) => {
  if (!communityId) {
    editedSettings.value = {}
    originalSettings.value = {}
    return
  }

  await fetchCommunitySettings(communityId)
}

const saveCommunitySettings = async () => {
  showConfirmDialog.value = true
}

const confirmSave = async () => {
  if (!communityForm.value) return
  const { valid } = await communityForm.value.validate()
  if (!valid) return

  saving.value = true
  error.value = ''

  try {
    await api.put(`/api/v1/community/${currentCommunityId.value}/`, editedSettings.value)
    originalSettings.value = { ...editedSettings.value }
    await fetchCommunities()
    showConfirmDialog.value = false
  } catch (err) {
    console.error('Failed to save community settings:', err)
    error.value = err.response?.data?.detail || 'Failed to save community settings'
  } finally {
    saving.value = false
  }
}

// Проверяем, были ли внесены изменения
const hasChanges = computed(() => {
  return Object.keys(editedSettings.value).some(key => 
    JSON.stringify(editedSettings.value[key]) !== JSON.stringify(originalSettings.value[key])
  )
})

onMounted(async () => {
  await userStore.fetchProfile()
  await fetchCommunities()
})
</script>

<style scoped>
.language-select {
  max-width: 140px;
}

.dialog-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
}

.settings-section {
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.settings-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.87);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.v-text-field :deep(.v-field__input) {
  font-size: 0.95rem;
}

.v-text-field :deep(.v-field__hint) {
  font-size: 0.8rem;
  opacity: 0.7;
}

.settings-section :deep(.v-switch__label) {
  font-size: 0.95rem;
}

.v-list-item {
  min-height: unset !important;
  padding: 8px !important;
}

.v-list-item :deep(.v-list-item__prepend) {
  padding-right: 8px;
}

.v-list-item :deep(.v-list-item-title) {
  margin-bottom: 2px;
}

:deep(.v-toolbar) {
  border-bottom: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style> 